import { useEffect, useMemo } from 'react'
import { ReportingPeriods } from '@common/interfaces/bbc'
import {
  TableNoteConfig,
  ICellNoteIndicator,
  getCellKey,
  getColumnIdsForPeriodLookup,
} from '@common/interfaces/notes'
import { buildRequestKey, dedupeInFlight } from '../helpers/dedupeInFlight'
import { normalizeColumnIdForNotes } from '../helpers/helpers'
import { listCellNoteIndicators } from '../redux/cellNotes/actions'
import {
  getCellNoteIndicatorsForTable,
  getCellNoteIndicatorsLoadingForTable,
  getCellNoteIndicatorsByPositionForTable,
  getSegmentationPendingForTable,
} from '../redux/cellNotes/selectors'
import { useSelector, useDispatch } from '../redux/hooks'
import { addNote } from '../redux/notes/actions'

/** When to ask again for markers withheld pending segmentation, from the save. */
const SEGMENTATION_REFETCH_DELAYS_MS = [4000, 12000]

export const useCellNotes = (config: TableNoteConfig) => {
  const dispatch = useDispatch()

  // Use clientName for Redux state selectors (for cross-workflow persistence)
  const clientId = config.clientId || ''
  const indicatorsData = useSelector(getCellNoteIndicatorsForTable(config.tableType, clientId))
  const isLoading = useSelector(getCellNoteIndicatorsLoadingForTable(config.tableType, clientId))
  const indicatorsByCell = useSelector(
    getCellNoteIndicatorsByPositionForTable(config.tableType, clientId),
  )
  const segmentationPendingAt = useSelector(
    getSegmentationPendingForTable(config.tableType, clientId),
  )

  // Create filter parameters for API call (still uses clientName for backend filtering)
  const filterParams = useMemo(
    () => ({
      tableType: config.tableType,
      ...(config.workflow && { workflow: config.workflow }),
      ...(config.rowIds && { rowIds: config.rowIds }),
      ...(config.columnIds && { columnIds: config.columnIds }),
      ...(config.globalColumnIds && {
        globalColumnIds: config.globalColumnIds,
      }),
      ...(config.recordDate &&
        (Array.isArray(config.recordDate) ? config.recordDate.length > 0 : true) && {
          recordDate: config.recordDate,
        }),
      ...(config.id && { id: config.id }),
    }),
    [
      config.tableType,
      config.workflow,
      config.rowIds,
      config.columnIds,
      config.globalColumnIds,
      config.recordDate,
      config.id,
    ],
  )

  // Load indicators on mount or when config changes
  useEffect(() => {
    if (config.clientId && config.enabled) {
      void dedupeInFlight(
        `cellNoteIndicators.${config.tableType}|${config.clientId}`,
        buildRequestKey(filterParams),
        () => dispatch(listCellNoteIndicators(filterParams)),
      )
    }
  }, [dispatch, filterParams, config.tableType, config.clientId, config.enabled])

  // A note naming several rows leaves its suggestions with the segmentation job, and they are
  // withheld until they have a slice — so the save response carries no markers for them and
  // the grid would sit empty until the page was reloaded, which is the hard-refresh complaint
  // this feature already fixed once. Ask again a few seconds later instead. Two attempts,
  // because the wait is a model call behind a queue and the first can land too early.
  useEffect(() => {
    if (!segmentationPendingAt || !config.clientId || !config.enabled) return

    const refetch = () => {
      dispatch(listCellNoteIndicators(filterParams))
    }
    const timers = SEGMENTATION_REFETCH_DELAYS_MS.map((delay) => setTimeout(refetch, delay))

    return () => timers.forEach(clearTimeout)
  }, [dispatch, segmentationPendingAt, filterParams, config.clientId, config.enabled])

  const periodView = config.periodView

  // Helper functions for checking cell notes (optional recordDate for multi-date views)
  const cellHasNotes = useMemo(
    () =>
      (rowId: string, columnId: string, recordDate?: string | null): boolean => {
        const columnIdsToCheck = getColumnIdsForPeriodLookup(
          columnId,
          periodView ?? ReportingPeriods.Monthly,
        )
        return columnIdsToCheck.some((cid) => {
          const cellKey = getCellKey(rowId, normalizeColumnIdForNotes(cid), recordDate)
          return !!(indicatorsByCell[cellKey] && indicatorsByCell[cellKey].noteCount > 0)
        })
      },
    [indicatorsByCell, periodView],
  )

  const getCellNoteCount = useMemo(
    () =>
      (rowId: string, columnId: string, recordDate?: string | null): number => {
        const columnIdsToCheck = getColumnIdsForPeriodLookup(
          columnId,
          periodView ?? ReportingPeriods.Monthly,
        )
        return columnIdsToCheck.reduce((sum, cid) => {
          const cellKey = getCellKey(rowId, normalizeColumnIdForNotes(cid), recordDate)
          return sum + (indicatorsByCell[cellKey]?.noteCount ?? 0)
        }, 0)
      },
    [indicatorsByCell, periodView],
  )

  const getCellNoteIndicator = useMemo(
    () =>
      (rowId: string, columnId: string, recordDate?: string | null): ICellNoteIndicator | null => {
        const columnIdsToCheck = getColumnIdsForPeriodLookup(
          columnId,
          periodView ?? ReportingPeriods.Monthly,
        )
        let merged: ICellNoteIndicator | null = null
        for (const cid of columnIdsToCheck) {
          const cellKey = getCellKey(rowId, normalizeColumnIdForNotes(cid), recordDate)
          const ind = indicatorsByCell[cellKey]
          if (ind) {
            if (!merged) {
              merged = { ...ind }
            } else {
              merged.noteCount = (merged.noteCount ?? 0) + (ind.noteCount ?? 0)
              merged.hasUnresolved = merged.hasUnresolved || ind.hasUnresolved
              if (
                ind.lastUpdated &&
                (!merged.lastUpdated || ind.lastUpdated > merged.lastUpdated)
              ) {
                merged.lastUpdated = ind.lastUpdated
              }
            }
          }
        }
        return merged
      },
    [indicatorsByCell, periodView],
  )

  /**
   * Attribution totals for a cell, summed across the columns a period roll-up covers (the
   * same lookup the note helpers use, so a quarterly view sees its months). Read from the
   * indicators already loaded for the whole table — no per-cell request.
   */
  const getCellAttribution = useMemo(
    () =>
      (
        rowId: string,
        columnId: string,
        recordDate?: string | null,
      ): {
        suggestedCount: number
        establishedCount: number
        establishedNoteCount: number
        confirmedLinkCount: number
      } => {
        const columnIdsToCheck = getColumnIdsForPeriodLookup(
          columnId,
          periodView ?? ReportingPeriods.Monthly,
        )
        return columnIdsToCheck.reduce(
          (totals, cid) => {
            const cellKey = getCellKey(rowId, normalizeColumnIdForNotes(cid), recordDate)
            const indicator = indicatorsByCell[cellKey]
            return {
              suggestedCount: totals.suggestedCount + (indicator?.suggestedCount ?? 0),
              establishedCount: totals.establishedCount + (indicator?.establishedCount ?? 0),
              establishedNoteCount:
                totals.establishedNoteCount + (indicator?.establishedNoteCount ?? 0),
              confirmedLinkCount: totals.confirmedLinkCount + (indicator?.confirmedLinkCount ?? 0),
            }
          },
          {
            suggestedCount: 0,
            establishedCount: 0,
            establishedNoteCount: 0,
            confirmedLinkCount: 0,
          },
        )
      },
    [indicatorsByCell, periodView],
  )

  // Create note function - uses workflow-specific id for context; cell recordDate overrides table-level
  const createNote = useMemo(
    () =>
      (
        cellCoords: {
          rowId: string
          columnId: string
          value: string
          recordDate?: string | null
        },
        noteData: Record<string, unknown>,
      ): unknown => {
        const { cellNoteEntityIds, ...restNoteData } = noteData
        const recordDate =
          cellCoords.recordDate ??
          (Array.isArray(config.recordDate) ? undefined : config.recordDate)

        const createData = {
          ...restNoteData,
          cellNoteContext: {
            tableType: config.tableType,
            rowId: cellCoords.rowId,
            columnId: normalizeColumnIdForNotes(cellCoords.columnId),
            value: cellCoords.value,
            recordDate: recordDate || undefined,
            ...((cellNoteEntityIds as Record<string, unknown>) || {}),
          },
        }

        return dispatch(addNote(createData))
      },
    [dispatch, config.tableType, config.recordDate],
  )

  return {
    // Indicators data (lightweight)
    indicators: indicatorsData?.indicators || [],
    indicatorsByCell,
    isLoading,

    cellHasNotes,
    getCellNoteCount,
    getCellNoteIndicator,
    getCellAttribution,
    createNote,
    config,
  }
}
